import { useState } from 'react';
import { Database, Smartphone, Eye, FileText, Server, Shield, ArrowLeft, ListChecks, Sparkles, ChevronDown, Download, Loader2, Crown, Lock, Gauge, MessageSquare } from 'lucide-react';
import { WebsiteAnalysis, api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { isMinimalAnalysisTier } from '../../shared/tiers';
import { ScoreCard } from './ScoreCard';
import { RecommendationsList } from './RecommendationsList';
import { ChatAssistant } from './ChatAssistant';
import { PremiumFeatureGate } from './PremiumFeatureGate';
import { PerformanceMetrics } from './PerformanceMetrics';
import VisibilityScore from './VisibilityScore';
import { AIOGEOScores } from './AIOGEOScores';
import { RevealCard } from './RevealCard';
import { usePersistedReveal } from '../hooks/usePersistedReveal';

interface ResultsViewProps {
  analysis: WebsiteAnalysis;
  onReset: () => void;
}

export function ResultsView({ analysis, onReset }: ResultsViewProps) {
  const [showRecommendations, setShowRecommendations] = usePersistedReveal('recommendations');
  const [showVisibility, setShowVisibility] = usePersistedReveal('visibility-score');
  const hasRecommendations = !!(analysis.recommendations && analysis.recommendations.length > 0);

  const { canExportReport, user, profile, isFree, isLoading: authLoading } = useAuth();
  // Free tier (and unauthenticated visitors) see only the four AI
  // discoverability scores plus an upgrade card. The shared helper
  // isMinimalAnalysisTier() is the single source of truth — we mirror it
  // on the client so the same rule decides what UI to show.
  // Auth is still loading? Hide the deep-report sections by default to
  // avoid briefly flashing them to a free user before their profile is
  // fetched. As a fallback, we also treat any analysis whose foundation
  // details are null (a server-side minimal scan) as minimal.
  const tier = profile?.subscriptionTier;
  const tierIsMinimal = authLoading || isFree || isMinimalAnalysisTier(tier);
  const payloadIsMinimal =
    analysis.status === 'completed' &&
    analysis.structuredDataDetails == null &&
    analysis.mobileFriendlyDetails == null;
  const isMinimalAnalysis = tierIsMinimal || payloadIsMinimal;
  const [exporting, setExporting] = useState<null | 'pdf' | 'docx'>(null);
  const [exportError, setExportError] = useState<string>('');
  // Show buttons whenever the user is entitled; disable them while the
  // analysis is still in flight so we don't try to export incomplete results.
  const showExportControls = canExportReport;
  const canExport = canExportReport && analysis.status === 'completed';
  // Only paying-but-not-entitled users see the upgrade hint. Entitled users
  // who happen to be on a non-completed analysis just see disabled buttons.
  const showUpgradeHint = !!user && !canExportReport;

  const handleExport = async (format: 'pdf' | 'docx') => {
    if (exporting) return;
    setExportError('');
    setExporting(format);
    try {
      const blob = await api.exportReport(analysis.id, format);
      const filenameBase = analysis.url
        .replace(/^https?:\/\//, '')
        .replace(/[^a-z0-9.-]+/gi, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || 'report';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `apex-analyst-${filenameBase}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke so the browser has time to start the download.
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (err: any) {
      setExportError(err?.message || `Failed to download ${format.toUpperCase()}.`);
    } finally {
      setExporting(null);
    }
  };
  const getOverallLabel = (score: number) => {
    if (score >= 80) return 'Excellent AI Readiness';
    if (score >= 60) return 'Good AI Readiness';
    if (score >= 30) return 'Needs Improvement';
    return 'Critical Issues Found';
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-8">
      <div className="bg-white rounded-2xl border border-gray-200 p-8">
        <button
          onClick={onReset}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-black mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Analyze another website
        </button>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Analysis Results</h2>
            <p className="text-sm text-gray-600 break-all">{analysis.url}</p>
          </div>

          {showExportControls ? (
            <div className="flex flex-col items-stretch sm:items-end gap-2">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleExport('pdf')}
                  disabled={!canExport || exporting !== null}
                  title={!canExport ? 'Available once the analysis finishes' : undefined}
                  className="inline-flex items-center gap-2 bg-black text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {exporting === 'pdf' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  {exporting === 'pdf' ? 'Preparing PDF…' : 'Download PDF'}
                </button>
                <button
                  type="button"
                  onClick={() => handleExport('docx')}
                  disabled={!canExport || exporting !== null}
                  title={!canExport ? 'Available once the analysis finishes' : undefined}
                  className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-800 text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {exporting === 'docx' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  {exporting === 'docx' ? 'Preparing Word…' : 'Download Word (.docx)'}
                </button>
              </div>
              {exportError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 max-w-xs">
                  {exportError}
                </div>
              )}
            </div>
          ) : showUpgradeHint ? (
            <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 inline-flex items-center gap-2 max-w-xs">
              <Crown className="w-4 h-4 text-yellow-500 flex-shrink-0" />
              <span>
                Download this report as PDF or Word —{' '}
                <a href="/?pricing=true" className="underline font-medium">
                  upgrade to Professional
                </a>
                .
              </span>
            </div>
          ) : null}
        </div>

        <div className="bg-gray-50 rounded-xl p-8 text-center border border-gray-200">
          <div className="inline-block">
            <div className="text-6xl font-bold text-black mb-2">
              {analysis.overallScore}
            </div>
            <div className="text-sm text-gray-500 mb-4">Overall Score</div>
            <div className="text-lg font-medium text-gray-700">
              {getOverallLabel(analysis.overallScore || 0)}
            </div>
          </div>
        </div>
      </div>

      <AIOGEOScores
        aioScore={analysis.aioScore ?? 0}
        aioTip={analysis.aioDetails?.tip || ''}
        geoScore={analysis.geoScore ?? 0}
        geoTip={analysis.geoDetails?.tip || ''}
        seoScore={analysis.technicalSeoScore ?? 0}
        seoTip={analysis.technicalSeoDetails?.tip || ''}
        aeoScore={analysis.aeoScore ?? 0}
        aeoTip={analysis.aeoDetails?.tip || ''}
      />

      {isMinimalAnalysis ? (
        <div className="bg-gradient-to-br from-gray-900 to-black text-white rounded-2xl p-8 border border-gray-800">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <Lock className="w-6 h-6 text-yellow-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                Unlock the full report
                <Crown className="w-5 h-5 text-yellow-400" />
              </h3>
              <p className="text-sm text-gray-300 mb-4">
                Your free scan includes the four AI discoverability scores above. Upgrade to a paid plan to also get:
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-200 mb-5">
                <li className="flex items-center gap-2"><Database className="w-4 h-4 text-gray-400" /> Structured data audit</li>
                <li className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-gray-400" /> Mobile-friendly check</li>
                <li className="flex items-center gap-2"><Eye className="w-4 h-4 text-gray-400" /> Accessibility review</li>
                <li className="flex items-center gap-2"><FileText className="w-4 h-4 text-gray-400" /> Content quality breakdown</li>
                <li className="flex items-center gap-2"><Shield className="w-4 h-4 text-gray-400" /> Privacy compliance scan</li>
                <li className="flex items-center gap-2"><ListChecks className="w-4 h-4 text-gray-400" /> Prioritized recommendations</li>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-gray-400" /> AI Visibility Score</li>
                <li className="flex items-center gap-2"><Gauge className="w-4 h-4 text-gray-400" /> Performance metrics &amp; Core Web Vitals</li>
                <li className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-gray-400" /> AI chat assistant for your results</li>
                <li className="flex items-center gap-2"><Download className="w-4 h-4 text-gray-400" /> PDF &amp; Word report exports</li>
              </ul>
              <a
                href="/?pricing=true"
                className="inline-flex items-center gap-2 bg-white text-black font-medium px-5 py-2.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Crown className="w-4 h-4 text-yellow-500" />
                See plans &amp; upgrade
              </a>
            </div>
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <ScoreCard
          title="Structured Data"
          score={analysis.structuredDataScore || 0}
          details={analysis.structuredDataDetails || {}}
          icon={<Database className="w-5 h-5" />}
          findings={analysis.structuredDataDetails?.findings || []}
        />
        <ScoreCard
          title="Mobile-Friendly"
          score={analysis.mobileFriendlyScore || 0}
          details={analysis.mobileFriendlyDetails || {}}
          icon={<Smartphone className="w-5 h-5" />}
          findings={analysis.mobileFriendlyDetails?.findings || []}
        />
        <ScoreCard
          title="Accessibility"
          score={analysis.accessibilityScore || 0}
          details={analysis.accessibilityDetails || {}}
          icon={<Eye className="w-5 h-5" />}
          findings={analysis.accessibilityDetails?.findings || []}
        />
        <ScoreCard
          title="Content Quality"
          score={analysis.contentQualityScore || 0}
          details={analysis.contentQualityDetails || {}}
          icon={<FileText className="w-5 h-5" />}
          findings={analysis.contentQualityDetails?.findings || []}
        />
        <ScoreCard
          title="Technical SEO"
          score={analysis.technicalSeoScore || 0}
          details={analysis.technicalSeoDetails || {}}
          icon={<Server className="w-5 h-5" />}
          findings={analysis.technicalSeoDetails?.findings || []}
        />
        <ScoreCard
          title="Privacy Compliance"
          score={analysis.privacyScore || 0}
          details={analysis.privacyDetails || {}}
          icon={<Shield className="w-5 h-5" />}
          findings={analysis.privacyDetails?.findings || []}
        />
      </div>
      )}

      {!isMinimalAnalysis && hasRecommendations && (
        <>
        <RevealCard
          icon={<ListChecks className="w-6 h-6" />}
          title="Recommendations"
          description={`${analysis.recommendations!.length} priority-ordered improvements to enhance your website's AI readiness.`}
          buttonLabel="View Recommendations"
          buttonIcon={<ChevronDown className="w-4 h-4" />}
          onReveal={() => setShowRecommendations(true)}
          onHide={() => setShowRecommendations(false)}
          revealed={showRecommendations}
        />
        {showRecommendations && (
          <RecommendationsList recommendations={analysis.recommendations} />
        )}
        </>
      )}

      {!isMinimalAnalysis && (
      <>
      <RevealCard
        icon={<Sparkles className="w-6 h-6" />}
        title="AI Visibility Score"
        description="Advanced Answer Engine Optimization (AEO) metrics showing how well your content performs across AI search platforms."
        buttonLabel="View AI Visibility Score"
        buttonIcon={<ChevronDown className="w-4 h-4" />}
        onReveal={() => setShowVisibility(true)}
        onHide={() => setShowVisibility(false)}
        revealed={showVisibility}
      />
      {showVisibility && (
        <PremiumFeatureGate
          feature="visibility_score"
          requiredTier="starter"
          featureName="AI Visibility Score"
          description="Advanced Answer Engine Optimization (AEO) metrics showing how well your content performs across AI search platforms"
        >
          <div className="bg-white rounded-2xl border border-gray-200 p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">AI Visibility Score</h2>
              <p className="text-sm text-gray-600">
                Comprehensive metrics for Answer Engine Optimization (AEO) performance
              </p>
            </div>
            <VisibilityScore
              aeoOverallScore={analysis.visibilityScore || 0}
              structuredDataScore={analysis.structuredDataScore || 0}
              snippetOptimizationScore={0}
              crawlabilityScore={0}
              featuredSnippetReadyScore={0}
              contentQualityScore={analysis.contentQualityScore || 0}
              technicalSeoScore={analysis.technicalSeoScore || 0}
              pagesAnalyzed={1}
              aeoSchemasCount={0}
              totalIssues={0}
              aiModelAccess={'0/3'}
            />
          </div>
        </PremiumFeatureGate>
      )}
      </>
      )}

      <PremiumFeatureGate
        feature="performance_metrics"
        requiredTier="professional"
        featureName="Performance Metrics"
        description="Deep dive into your website's loading speed, Core Web Vitals, and performance scores with Google PageSpeed Insights"
      >
        <PerformanceMetrics
          analysisId={String(analysis.id)}
          performanceScore={0}
          performanceMetrics={{}}
        />
      </PremiumFeatureGate>

      <PremiumFeatureGate
        feature="ai_chat"
        requiredTier="professional"
        featureName="AI Assistant"
        description="Get instant answers about your analysis and personalized improvement strategies from our AI expert"
      >
        <ChatAssistant analysisId={String(analysis.id)} />
      </PremiumFeatureGate>

    </div>
  );
}
